import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';
import PrivacyPause from '../components/PrivacyPause';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AccountPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saving, setSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const getHeaders = () => {
    const userId = localStorage.getItem('userId');
    return userId ? { 'X-User-Id': userId } : {};
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/auth/me`,
        { withCredentials: true, headers: getHeaders() }
      );
      setUser(response.data);
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwords.newPassword !== passwords.confirmPassword) {
      setMessage({ type: 'error', text: 'كلمتا المرور غير متطابقتين' });
      return;
    }

    if (passwords.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' });
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        {
          current_password: passwords.currentPassword,
          new_password: passwords.newPassword
        },
        { withCredentials: true, headers: getHeaders() }
      );
      
      setMessage({ type: 'success', text: '✅ تم تغيير كلمة المرور بنجاح' });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'فشل تغيير كلمة المرور' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userId');
    localStorage.removeItem('isLoggedIn');
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--light-bg)'
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        padding: '16px 20px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <button 
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer'
          }}
        >
          →
        </button>
        <h1 style={{ 
          fontSize: '20px', 
          color: 'var(--primary)',
          margin: 0 
        }}>
          حسابي
        </h1>
        <div style={{ width: '24px' }}></div>
      </div>

      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        {/* User Info Card */}
        <div className="card" style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px',
            marginBottom: '20px'
          }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--primary), var(--purple))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              color: 'white'
            }}>
              👤
            </div>
            <div>
              <h2 style={{ 
                fontSize: '20px', 
                color: 'var(--text-dark)',
                marginBottom: '4px'
              }}>
                {user?.name || 'مستخدم'}
              </h2>
              <p style={{ 
                color: 'var(--text-light)', 
                fontSize: '14px',
                margin: 0 
              }}>
                {user?.email}
              </p>
            </div>
          </div>

          {/* Account Details */}
          <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              padding: '12px 0',
              borderBottom: '1px solid #f5f5f5'
            }}>
              <span style={{ color: 'var(--text-light)' }}>تاريخ التسجيل</span>
              <span style={{ color: 'var(--text-dark)' }}>
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('ar-SA') : '-'}
              </span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              padding: '12px 0',
              borderBottom: '1px solid #f5f5f5'
            }}>
              <span style={{ color: 'var(--text-light)' }}>نوع الحساب</span>
              <span style={{ 
                color: user?.subscription_type === 'premium' ? 'var(--gold)' : 'var(--text-dark)',
                fontWeight: user?.subscription_type === 'premium' ? '600' : '400'
              }}>
                {user?.subscription_type === 'premium' ? '⭐ مميز' : 'مجاني'}
              </span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              padding: '12px 0'
            }}>
              <span style={{ color: 'var(--text-light)' }}>حالة الملف</span>
              <span style={{ 
                color: user?.profile_completed ? 'var(--success)' : 'var(--error)'
              }}>
                {user?.profile_completed ? '✅ مكتمل' : '⚠️ غير مكتمل'}
              </span>
            </div>
          </div>
        </div>

        {/* Privacy Pause Section */}
        <PrivacyPause />

        {/* Change Password Section */}
        <div className="card">
          <div 
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              padding: '4px 0'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '24px' }}>🔐</span>
              <span style={{ fontSize: '16px', color: 'var(--text-dark)' }}>
                تغيير كلمة المرور
              </span>
            </div>
            <span style={{ 
              transform: showPasswordForm ? 'rotate(90deg)' : 'rotate(0)',
              transition: 'transform 0.3s ease'
            }}>
              ←
            </span>
          </div>

          {showPasswordForm && (
            <form onSubmit={handlePasswordChange} style={{ marginTop: '20px' }}>
              {message.text && (
                <div style={{
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  background: message.type === 'error' ? '#FEE2E2' : '#D1FAE5',
                  color: message.type === 'error' ? '#DC2626' : '#059669',
                  fontSize: '14px'
                }}>
                  {message.text}
                </div>
              )}

              <div className="form-group password-field">
                <label className="form-label">كلمة المرور الحالية</label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="input password-input"
                    value={passwords.currentPassword}
                    onChange={(e) => setPasswords({...passwords, currentPassword: e.target.value})}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    aria-label={showCurrentPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                  >
                    {showCurrentPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                  </button>
                </div>
              </div>

              <div className="form-group password-field">
                <label className="form-label">كلمة المرور الجديدة</label>
                <div className="password-input-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className="input password-input"
                    value={passwords.newPassword}
                    onChange={(e) => setPasswords({...passwords, newPassword: e.target.value})}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                  >
                    {showNewPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                  </button>
                </div>
              </div>

              <div className="form-group password-field">
                <label className="form-label">تأكيد كلمة المرور الجديدة</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="input password-input"
                    value={passwords.confirmPassword}
                    onChange={(e) => setPasswords({...passwords, confirmPassword: e.target.value})}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
> 
                    {showConfirmPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ width: '100%', marginTop: '8px' }}
              >
                {saving ? 'جاري الحفظ...' : 'حفظ كلمة المرور الجديدة'}
              </button>
            </form>
          )}
        </div>

        {/* Other Options */}
        <div className="card" style={{ marginTop: '16px' }}>
          <button
            onClick={() => navigate('/profile-setup')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 0',
              background: 'none',
              border: 'none',
              borderBottom: '1px solid #f5f5f5',
              cursor: 'pointer',
              fontSize: '16px',
              color: 'var(--text-dark)'
            }}
          >
            <span style={{ fontSize: '24px' }}>📝</span>
            تعديل مواصفاتي
          </button>

          <button
            onClick={() => navigate('/photos')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 0',
              background: 'none',
              border: 'none',
              borderBottom: '1px solid #f5f5f5',
              cursor: 'pointer',
              fontSize: '16px',
              color: 'var(--text-dark)'
            }}
          >
            <span style={{ fontSize: '24px' }}>📷</span>
            إدارة الصور
          </button>

          <button
            onClick={() => navigate('/pricing')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 0',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              color: 'var(--text-dark)'
            }}
          >
            <span style={{ fontSize: '24px' }}>⭐</span>
            ترقية الحساب
          </button>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            marginTop: '20px',
            padding: '14px',
            background: 'transparent',
            border: '2px solid var(--error)',
            borderRadius: '12px',
            color: 'var(--error)',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          تسجيل الخروج
        </button>
      </div>
    </div>
  );
}

export default AccountPage;
